import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePostAuthApi, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { useQueryClient } from '@tanstack/react-query';
import { DonationPageConfig } from '../PublicDonation/types';
import styles from './DonationsAdmin.module.css';

interface Props {
  fundId: number;
}

const MAX_SIZE_KB = 2048;

const LogoUploader = ({ fundId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: config } = useGetAuthApi<DonationPageConfig>(`donations/page-config/${fundId}`);
  const { mutateAsync: upload, isPending: uploading } = usePostAuthApi<{ logoUrl: string }, { fileName: string; contentBase64: string }>(
    `donations/page-config/${fundId}/logo`
  );
  const { mutateAsync: deleteLogo, isPending: deleting } = useDeleteAuthApi<void>(`donations/page-config/${fundId}/logo`);

  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config?.logoUrl) setPreview(config.logoUrl);
  }, [config?.logoUrl]);

  const handleFile = async (file: File) => {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError(t('donations.admin.logo.invalidType'));
      return;
    }

    if (file.size > MAX_SIZE_KB * 1024) {
      setError(t('donations.admin.logo.tooLarge', { max: MAX_SIZE_KB }));
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(',')[1];
      try {
        const { logoUrl } = await upload({ fileName: file.name, contentBase64: base64 });
        setPreview(logoUrl);
        queryClient.invalidateQueries({ queryKey: [`donations/page-config/${fundId}`] });
      } catch {
        setError(t('donations.admin.logo.uploadError'));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteLogo();
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: [`donations/page-config/${fundId}`] });
    } catch {
      setError(t('donations.admin.logo.deleteError'));
    }
  };

  const busy = uploading || deleting;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('donations.admin.logo.title')}</h2>
      <p className={styles.panelSubtitle}>{t('donations.admin.logo.subtitle')}</p>

      <div className={styles.logoRow}>
        {preview && (
          <div className={styles.logoPreviewWrap}>
            <img
              src={preview}
              alt="logo"
              className={`${styles.logoPreview} ${busy ? styles.logoBusy : ''}`}
            />
          </div>
        )}

        <div className={styles.logoActions}>
          <div className={styles.logoButtons}>
            <button
              type="button"
              className={`${styles.primaryBtn} ${styles.logoUploadBtn}`}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {uploading
                ? t('donations.admin.logo.uploading')
                : preview
                  ? t('donations.admin.logo.change')
                  : t('donations.admin.logo.upload')}
            </button>
            {preview && (
              <button
                type="button"
                className={`${styles.primaryBtn} ${styles.logoDeleteBtn}`}
                disabled={busy}
                onClick={handleDelete}
              >
                {deleting ? t('donations.admin.logo.deleting') : t('donations.admin.logo.remove')}
              </button>
            )}
          </div>
          <span className={styles.logoHint}>{t('donations.admin.logo.hint')}</span>
        </div>
      </div>

      {error && <div className={styles.logoError}>{error}</div>}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className={styles.logoFileInput}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default LogoUploader;
